import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import type { WishlistItem } from '@/modules/wishlist/domain/entities/WishlistItem';
import { HttpWishlistRepository } from '@/modules/wishlist/infrastructure/repositories/HttpWishlistRepository';
import { useToastStore } from './toast';
import { useAuthStore } from './auth';
import { getApiErrorMessage } from '@/utils/apiError';
import { startOAuthLogin } from '@/services/OAuthLoginNavigation';

const repository = new HttpWishlistRepository();

export const useWishlistStore = defineStore('wishlist', () => {
  const router = useRouter();
  const items = ref<WishlistItem[]>([]);
  const itemIds = ref<Set<string>>(new Set());
  const loading = ref(false);
  const initialized = ref(false);
  const error = ref<string | null>(null);
  const toastStore = useToastStore();

  const itemsCount = computed(() => items.value.length);
  const isEmpty = computed(() => items.value.length === 0);

  const ensureItemIdsSet = (): Set<string> => {
    if (itemIds.value instanceof Set) {
      return itemIds.value;
    }

    itemIds.value = new Set(Array.isArray(itemIds.value) ? itemIds.value : []);
    return itemIds.value;
  };

  // Check if an item is in the wishlist
  const isInWishlist = (entityType: string, entityId: string): boolean => {
    const key = `${entityType}:${entityId}`;
    return ensureItemIdsSet().has(key);
  };

  // Initialize - idempotent, no-op if already initialized
  const init = () => {
    if (initialized.value) return;
    
    const authStore = useAuthStore();
    if (!authStore.token) {
      // Not authenticated, mark as initialized without loading
      initialized.value = true;
      return;
    }

    // Will fetch from API
    fetchWishlist();
  };

  // Fetch wishlist from API
  const fetchWishlist = async (page = 1, perPage = 15) => {
    const authStore = useAuthStore();
    if (!authStore.token) {
      initialized.value = true;
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const data = await repository.getWishlist(page, perPage);
      items.value = data.items;
      updateItemIds();
      initialized.value = true;
    } catch (err: unknown) {
      error.value = getApiErrorMessage(err, 'Failed to load wishlist');
    } finally {
      loading.value = false;
    }
  };

  // Toggle item in wishlist (add or remove)
  const toggleItem = async (
    entityType: string,
    entityId: string,
    productInfo?: { name: string; price: number; image?: string | null; isActive?: boolean }
  ) => {
    const authStore = useAuthStore();

    // If not authenticated, redirect to login
    if (!authStore.token) {
      const currentPath = router.currentRoute.value.fullPath;
      void startOAuthLogin(currentPath);
      return null;
    }

    const key = `${entityType}:${entityId}`;
    const itemIdsSet = ensureItemIdsSet();
    const isCurrentlyInWishlist = itemIdsSet.has(key);
    const previousItems = [...items.value];
    const previousItemIds = new Set(itemIdsSet);

    loading.value = true;
    error.value = null;

    try {
      // Optimistic update
      if (isCurrentlyInWishlist) {
        // Remove from local state immediately
        items.value = items.value.filter(
          item => !(item.productEntityType === entityType && item.productEntityId === entityId)
        );
        itemIdsSet.delete(key);
      } else {
        // Add to local state immediately
        const newItem: WishlistItem = {
          id: `temp_${Date.now()}`,
          productEntityType: entityType,
          productEntityId: entityId,
          addedAt: new Date().toISOString(),
          productInfo: productInfo
            ? {
                entityType,
                entityId,
                name: productInfo.name,
                price: productInfo.price,
                image: productInfo.image ?? null,
                isActive: productInfo.isActive ?? true,
                slug: null,
                code: null,
              }
            : null,
        };
        items.value.unshift(newItem);
        itemIdsSet.add(key);
      }

      // Call API
      const result = await repository.toggleItem(entityType, entityId);

      // Update with actual server response
      if (result.inWishlist && result.action === 'added') {
        // Refresh to get the real ID and data
        await fetchWishlist(1, items.value.length + 5);
      }

      toastStore.addToast(
        result.action === 'added' ? 'Added to wishlist' : 'Removed from wishlist'
      );

      return result;
    } catch (err: unknown) {
      // Rollback on error
      error.value = getApiErrorMessage(err, 'Failed to update wishlist');
      items.value = previousItems;
      itemIds.value = previousItemIds;
      toastStore.addToast(error.value!, 'error');
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Remove item from wishlist
  const removeItem = async (entityType: string, entityId: string) => {
    const key = `${entityType}:${entityId}`;
    const previousItems = [...items.value];
    const itemIdsSet = ensureItemIdsSet();
    const previousItemIds = new Set(itemIdsSet);

    loading.value = true;
    error.value = null;

    try {
      // Optimistic removal
      items.value = items.value.filter(
        item => !(item.productEntityType === entityType && item.productEntityId === entityId)
      );
      itemIdsSet.delete(key);

      // Call API
      await repository.removeItem(entityType, entityId);

      toastStore.addToast('Item removed from wishlist');
    } catch (err: unknown) {
      // Rollback on error
      error.value = getApiErrorMessage(err, 'Failed to remove item');
      items.value = previousItems;
      itemIds.value = previousItemIds;
      toastStore.addToast(error.value!, 'error');
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Clear entire wishlist
  const clearWishlist = async () => {
    const previousItems = [...items.value];
    const itemIdsSet = ensureItemIdsSet();
    const previousItemIds = new Set(itemIdsSet);

    loading.value = true;
    error.value = null;

    try {
      // Optimistic clear
      items.value = [];
      itemIdsSet.clear();

      // Call API
      const result = await repository.clearWishlist();

      toastStore.addToast('Wishlist cleared');
      return result;
    } catch (err: unknown) {
      // Rollback on error
      error.value = getApiErrorMessage(err, 'Failed to clear wishlist');
      items.value = previousItems;
      itemIds.value = previousItemIds;
      toastStore.addToast(error.value!, 'error');
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Share wishlist
  interface ShareResult {
    shareUrl: string;
    expiresAt: string;
  }

  const shareWishlist = async (): Promise<ShareResult | null> => {
    loading.value = true;
    error.value = null;

    try {
      const result = await repository.shareWishlist();
      toastStore.addToast('Share link copied to clipboard');
      return result;
    } catch (err: unknown) {
      error.value = getApiErrorMessage(err, 'Failed to generate share link');
      toastStore.addToast(error.value!, 'error');
      return null;
    } finally {
      loading.value = false;
    }
  };

  // Check multiple items
  const checkItems = async (itemsToCheck: { entityType: string; entityId: string }[]) => {
    const authStore = useAuthStore();
    if (!authStore.token || itemsToCheck.length === 0) {
      return {};
    }

    try {
      const results = await repository.checkItems(itemsToCheck);
      const itemIdsSet = ensureItemIdsSet();
      // Update local state based on results
      Object.entries(results).forEach(([key, inWishlist]) => {
        if (inWishlist) {
          itemIdsSet.add(key);
        } else {
          itemIdsSet.delete(key);
        }
      });
      return results;
    } catch (err: unknown) {
      console.error('Failed to check wishlist items:', err);
      return {};
    }
  };

  // Reset store (called on logout)
  const reset = () => {
    items.value = [];
    ensureItemIdsSet().clear();
    loading.value = false;
    initialized.value = false;
    error.value = null;
  };

  // Helper to update itemIds Set from items array
  const updateItemIds = () => {
    itemIds.value = new Set(
      items.value.map(item => `${item.productEntityType}:${item.productEntityId}`)
    );
  };

  return {
    items,
    itemIds,
    loading,
    initialized,
    error,
    itemsCount,
    isEmpty,
    isInWishlist,
    init,
    fetchWishlist,
    toggleItem,
    removeItem,
    clearWishlist,
    shareWishlist,
    checkItems,
    reset,
  };
});
